import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { RootState } from './utils/experienceStore';
import { DEVICE_DEFINITIONS, DEFAULT_DEVICE } from './deviceDefinitions';

interface DeviceAwareImageProps {
    src: string;
    alt?: string;
    className?: string;
    maxWidth?: number;
    maxHeight?: number;
    showDimensions?: boolean;
    onClick?: () => void;
    isHovering?: boolean;
}

const StyledImage = styled.img<{
    isHovering: boolean;
    deviceScale: number;
    maxWidth?: number;
    maxHeight?: number;
}>`
    object-fit: contain;
    background-color: #ffffff;
    background-image: linear-gradient(45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(-45deg, #dbdbdb 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #dbdbdb 75%),
        linear-gradient(-45deg, transparent 75%, #dbdbdb 75%);
    background-size: 20px 20px;
    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
    transform: scale(0.95);
    transition: transform 0.3s ease;

    /* Apply device scaling to the image display */
    width: ${(props) =>
        props.maxWidth ? `${props.maxWidth / props.deviceScale}px` : 'auto'};
    height: ${(props) =>
        props.maxHeight ? `${props.maxHeight / props.deviceScale}px` : 'auto'};
    max-width: ${(props) =>
        props.maxWidth ? `${props.maxWidth / props.deviceScale}px` : 'none'};
    max-height: ${(props) =>
        props.maxHeight ? `${props.maxHeight / props.deviceScale}px` : 'none'};
`;

export default function DeviceAwareImage({
    src,
    alt = '',
    className = '',
    maxWidth,
    maxHeight,
    showDimensions = false,
    onClick,
    isHovering = false,
}: DeviceAwareImageProps) {
    const { experienceDevices, activeTab } = useSelector(
        (state: RootState) => state.canvas,
    );
    const [imageWidth, setImageWidth] = useState<number | null>(null);
    const [imageHeight, setImageHeight] = useState<number | null>(null);

    // Get device scale factor for current experience
    const currentDevice =
        experienceDevices[activeTab] || experienceDevices.main;
    const device =
        DEVICE_DEFINITIONS.find((d) => d.id === currentDevice.deviceId) ||
        DEFAULT_DEVICE;
    const deviceScale = device.scale || 1;

    useEffect(() => {
        if (src) {
            const img = new Image();
            img.src = src;
            img.onload = () => {
                setImageWidth(img.width);
                setImageHeight(img.height);
            };
        }
    }, [src]);

    // Note: We only show pixel dimensions now, no point conversion needed

    return (
        <div className={className}>
            <StyledImage
                src={src}
                alt={alt}
                isHovering={isHovering}
                deviceScale={deviceScale}
                maxWidth={maxWidth}
                maxHeight={maxHeight}
                onClick={onClick}
            />
            {showDimensions && imageWidth && imageHeight && (
                <div className="text-sm text-gray-600 dark:text-gray-300 text-center mt-1">
                    {imageWidth}px × {imageHeight}px
                </div>
            )}
        </div>
    );
}
